import { PureComponent } from 'react';
import { SlideFromRightWrapper } from '../../../../common/SlideFromRightWrapper';
import WeightToHeight from './growthChart/WeightToHeight';
import { connect } from 'react-redux';
import { GlobalState } from '../../../../../store/reducers';
import {
    getPatientDetailsVisits,
    getPatientPersonalProfile,
    getPrescription,
    getRxDoctor,
    getVitals,
} from '../../../../../store/selectors/prescriptionPad';
import {
    Doctor,
    PatientPersonalProfile,
    Prescription,
    Tool,
    VitalsEntity,
} from '../../../../../store/types/prescription';
import {
    getAgeInYearsWithoutRoundOff,
    getAgeString,
    smoothScrollToItemWithDelay,
} from '../../../../../store/utils';
import WeightToAge from './growthChart/WeightToAge';
import BMIToAge from './growthChart/BMIToAge';
import HeightToAge from './growthChart/HeightToAge';
import OFCToAge from './growthChart/OFCToAge';
import { Switch } from 'antd';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../../../store/actions';
import { deleteVitals, updateVitals } from '../../../../../store/actions/prescriptionPad';
import { removeUnwatedKeys } from './VitalAutoSuggest';
import GrowthVitals from './GrowthVitals';
import debounce from 'lodash/debounce';
import { ONE_CHAR_GENDER } from '../../../../../pages/NewPatient';
import {
    BMI_TO_AGE,
    HEIGHT_TO_AGE,
    OFC_TO_AGE,
    WEIGHT_TO_AGE,
    WEIGHT_TO_HEIGHT,
    getWhiteListedGrowthChartVitalsByAge,
} from './growthChart/constants/utils';
import Hotkeys from 'react-hot-keys';
import { GROWTH_CHART_TYPE } from './growthChart/constants/growthChartType';
import { fetchAndStoreGrowthChartData, getAddPDForms } from '../../../../../pages/utils';
import moment from 'moment';
import { getDoc, getSelectedClinicId } from '../../../../../store/idbHandler';
import { track } from '../../../../../tracking';
import { getHeaderAndFooterContent } from '../../../leftPanel/DentalChart/Utils';

type Props = {
    closeMe: () => void;
    patientPersonalProfile?: PatientPersonalProfile;
    vitals: VitalsEntity[];
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    visits?: (Tool & {
        date: string;
        visitId: string;
        precriptionId?: string | undefined;
        flavour?: string | undefined;
        translateLang?: string | undefined;
    })[];
    prescription?: Prescription;
    doctor?: Doctor;
};

type State = {
    selectedTab?: SelectedTabType;
    isPreTerm?: boolean;
    growthChartData?: GROWTH_CHART_TYPE;
    showGestationalError: string;
};

type SelectedTabType =
    | 'weight-for-height'
    | 'weight-for-age'
    | 'ofc-for-age'
    | 'height-for-age'
    | 'bmi-for-age';

const GROWTH_CHART_PRINT_TYPE = 'growth-chart-print';

class GrowthChartRightPopUp extends PureComponent<Props, State> {
    state: State = {
        selectedTab: 'weight-for-age',
        isPreTerm: false,
        growthChartData: window?.growthChartData,
        showGestationalError: '',
    };

    selectedClinic = getSelectedClinicId();
    docData = getDoc();

    async componentDidMount() {
        const growthChartData = await fetchAndStoreGrowthChartData();

        this.setState({
            growthChartData: growthChartData,
        });

        this.setGestationalAgeToggle();
    }
    selectGraphSection = (selectedTab: SelectedTabType) => {
        this.setState({ selectedTab: selectedTab });

        smoothScrollToItemWithDelay(selectedTab, 10);
    };

    onElementUpdate = (option: VitalsEntity, index?: number) => {
        const { dispatch } = this.props;
        dispatch(updateVitals(removeUnwatedKeys(option), index));
    };

    onElementDelete = (id: string) => {
        const { dispatch } = this.props;
        dispatch(deleteVitals(id));
    };

    onPretermChange = (checked: boolean) => {
        this.setState({ isPreTerm: checked, selectedTab: 'weight-for-age' });
        if (!checked) {
            // delete gestational age
            this.onElementDelete('lb-5257276261');
        } else {
            this.setGestationalAge();
        }
    };

    setGestationalAgeToggle = () => {
        const { vitals } = this.props;
        const gestationalAge = vitals?.find((v) => v.id === 'lb-5257276261');
        if (gestationalAge) {
            this.setState({ isPreTerm: true });
        } else {
            this.setState({ isPreTerm: false });
        }
    };

    handleScroll = () => {
        const divIds = [
            'weight-for-height',
            'weight-for-age',
            'ofc-for-age',
            'height-for-age',
            'bmi-for-age',
        ];

        const windowHeight = window.innerHeight || document.documentElement.clientHeight;

        divIds.forEach((id) => {
            const div = document.getElementById(id);
            if (!div) return;

            const rect = div.getBoundingClientRect();
            const isInCenter = rect.top <= windowHeight / 2 && rect.bottom >= windowHeight / 2;

            if (isInCenter) {
                this.setState({ selectedTab: id as SelectedTabType });
            }
        });
    };

    setGestationalAge = () => {
        const { vitals, visits } = this.props;

        const vitalData = visits?.[visits.length - 1]?.vitals?.find(
            (vital) => vital.id === 'lb-5257276261',
        );

        if (vitalData) {
            const daysPassed = moment(new Date()).diff(visits?.[visits.length - 1].date, 'days');

            if (Number(vitalData.value.qt) + Math.abs(Number(daysPassed)) <= 350) {
                const option = {
                    ...vitalData,
                    value: {
                        ...vitalData.value,
                        qt: (Number(vitalData.value.qt) + Number(daysPassed))?.toString(),
                    },
                };
                if (option.value.qt) {
                    const index = vitals.findIndex((el) => el.id === option.id);
                    this.onElementUpdate(option, index);
                }
            } else {
                this.setState({
                    showGestationalError:
                        'The maximum gestational age allowed is 50 weeks and 0 days. Plz turn off gestational growth chart',
                });
            }
        }
    };

    debouncedHandleScroll = debounce(this.handleScroll, 50);

    printGraph = async () => {
        const weightToAgeCanvas = document.getElementById(WEIGHT_TO_AGE) as HTMLCanvasElement;
        const heightToAgeCanvas = document.getElementById(HEIGHT_TO_AGE) as HTMLCanvasElement;
        const bmiToAgeCanvas = document.getElementById(BMI_TO_AGE) as HTMLCanvasElement;
        const ofcToAgeCanvas = document.getElementById(OFC_TO_AGE) as HTMLCanvasElement;
        const weightToHeightCanvas = document.getElementById(WEIGHT_TO_HEIGHT) as HTMLCanvasElement;
        const ptFormDets = await getAddPDForms();
        const selectedClinicData =
            this.docData?.profile.professional.clinics?.find(
                (clinic) => clinic._id === this.selectedClinic,
            ) || this.docData?.profile?.professional?.clinics?.[0];

        const patientDetails = [
            this.props.patientPersonalProfile?.gender,
            getAgeString(
                {
                    age: this.props.patientPersonalProfile?.age?.value,
                    month: this.props.patientPersonalProfile?.age?.month,
                    dob: this.props.patientPersonalProfile?.age?.dob,
                },
                ' ',
            ),
            `${this.props.patientPersonalProfile?.phone?.c || ''}${
                this.props.patientPersonalProfile?.phone?.n || ''
            }`,
        ].filter(Boolean);

        const pdData = patientDetails.map((detail, i) => {
            return detail;
        });

        const printWindow = document.getElementById(GROWTH_CHART_PRINT_TYPE) as HTMLIFrameElement;

        if (!printWindow) {
            return;
        }
        printWindow.src = 'about:blank';
        const iframeDocument = printWindow.contentDocument || printWindow.contentWindow?.document;

        const { prescription, doctor } = this.props;
        if (!doctor) {
            return;
        }
        const { headerHtml, footerHtml } = getHeaderAndFooterContent(
            doctor,
            prescription?.id || '',
            ptFormDets,
        );

        iframeDocument?.open();
        iframeDocument?.write(` <!DOCTYPE html>
                                         <html>
                                            <head>
                                                <title>Print</title>
                                                <style>
                                                @import url('https://fonts.googleapis.com/css2?family=Anek+Bangla:wght@100;200;300;400;500;600;700;800&family=Baloo+Tamma+2:wght@400;500;600;700;800&family=Hind+Guntur:wght@300;400;500;600;700&family=Hind+Madurai:wght@300;400;500;600;700&family=Mukta+Mahee:wght@200;300;400;500;600;700;800&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&family=Rasa:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap');
                                               
                                                
                                                *, ::before, ::after {
                                                        box-sizing: border-box;
                                                        margin: 0px;
                                                        padding: 0px;
                                                }

                                                html{
                                                    font-size: 16px;
                                                }
                                                
                                                body {
                                                    font-family:  'Poppins', sans-serif;
                                                }

                                                @media print { @page { margin: 0; margin-right: 0px; } 

                                                body { padding: 20px 24px 36px 24px; height: 100vh } }

                                                .grid-container {
                                                    display: grid;
                                                    grid-template-columns: auto auto;
                                                    background-color: #2196F3;
                                                    padding: 2px 10px;
                                                  }
                                                .grid-item {
                                                    background-color: rgba(255, 255, 255, 0.8);
                                                    padding: 2px 20px;
                                                    font-size: 30px;
                                                    text-align: center;
                                                }

                                                .flex {
                                                    display: flex;
                                                }
                                                
                                                .justify-between {
                                                    justify-content: space-between;
                                                }

                                                .space-x-4> :not([hidden])~ :not([hidden]) {
                                                    --tw-space-x-reverse: 0;
                                                    margin-right: calc(0.25rem * var(--tw-space-x-reverse));
                                                    margin-left: calc(0.25rem * calc(1 - var(--tw-space-x-reverse)));
                                                }

                                                .px-12{
                                                    padding-left: 0.75rem;
                                                    padding-right: 0.75rem;
                                                }

                                                .pb-4{
                                                    padding-bottom: 0.25rem;
                                                }

                                                .pt-8{
                                                    padding-top: 0.5rem;
                                                }

                                                .justify-between {
                                                    justify-content: space-between;
                                                }

                                                .border-b {
                                                    border-bottom: 1px solid;
                                                }

                                                .header-bottom-border{
                                                    border-color: rgb(6,6,6);
                                                }

                                                .w-60p{
                                                    width: 60%;
                                                }

                                                .items-start {
                                                    align-items: flex-start;
                                                }

                                                .space-x-32> :not([hidden])~ :not([hidden]) {
                                                    --tw-space-x-reverse: 0;
                                                    margin-right: calc(2rem * var(--tw-space-x-reverse));
                                                    margin-left: calc(2rem * calc(1 - var(--tw-space-x-reverse)));
                                                }

                                                .w-64 {
                                                    width: 4rem;
                                                }
    
                                                .py-4{
                                                    padding-top: 0.25rem;
                                                    padding-bottom: 0.25rem;
                                                }
    
                                                .space-y-6 > :not([hidden]) ~ :not([hidden]){
                                                    --tw-space-y-reverse: 0;
                                                    margin-top: calc(0.375rem * calc(1 - var(--tw-space-y-reverse)));
                                                    margin-bottom: calc(0.375rem * var(--tw-space-y-reverse));
                                                }
    
                                                .text-left {
                                                    text-align: left;
                                                }
    
                                                .body-1 {
                                                    font-size: 0.85rem;
                                                    line-height: 1.25rem;
                                                }
                                                
                                                .text-docM-purple-400 {
                                                    --tw-text-opacity: 1;
                                                    color: rgba(117, 72, 200, var(--tw-text-opacity));
                                                }
                                                .bold {
                                                    font-weight: 700;
                                                }
    
                                                .whitespace-preline{
                                                    white-space: pre-line;
                                                }
    
                                                .text-right{
                                                    text-align: right;
                                                }
                                    
                                                .w-40p{
                                                    width: 40%;
                                                }

                                                .text-darwin-neutral-900 {
                                                    color: rgb(88,88,88);
                                                }

                                                .items-end {
                                                    align-items: flex-end;
                                                }

                                                .text-9{
                                                    font-size: .5625rem;
                                                    line-height: .75rem;
                                                }

                                                .w-72{
                                                    width: 4.5rem;
                                                }

                                                .flex-col {
                                                    flex-direction: column;
                                                }

                                                .max-w-92{
                                                    max-width: 5.75rem;
                                                }

                                                .object-fit-contain{
                                                    object-fit: contain;
                                                }

                                                .h-60{
                                                    height: 3.75rem;
                                                }

                                                .text-11 {
                                                    font-size: 0.68rem;
                                                    line-height: 1rem;
                                                }

                                                .text-13 {
                                                    font-size: 0.85rem;
                                                    line-height: 1.25rem;
                                                }                                    
                                                </style>
                                            </head>
                                      <body>
                                      <div class="h-full w-full flex flex-col">
                                        ${headerHtml}
                                      <div class="flex-1 space-y-12">
                                      <div class="flex justify-between pt-8">
                                        <div class="text-13">
                                                <span class="text-13 bold">${
                                                    this.props.patientPersonalProfile?.name || ''
                                                }, </span>
                                                ${pdData}
                                        </div>
                                        <div class="text-13 bold">
                                        
                                          ${this.state?.isPreTerm ? '[Fenton]' : '[WHO/IAP]'}
                                        </div>
                                      </div>
                                      <div class="grid-container">
                                        ${
                                            weightToAgeCanvas?.toDataURL()
                                                ? `<div class="grid-item">
                                                           <span style="font-size: 16px;">Weight For Age</span>
                                                           <img width="100%" height="80%" src="${weightToAgeCanvas?.toDataURL()}" />
                                                    </div>`
                                                : ''
                                        }
                                        ${
                                            heightToAgeCanvas?.toDataURL()
                                                ? `<div class="grid-item">
                                                           <span style="font-size: 16px;">Height For Age</span>
                                                           <img width="100%" height="80%" src="${heightToAgeCanvas?.toDataURL()}" />
                                                    </div>`
                                                : ''
                                        }
                                        ${
                                            bmiToAgeCanvas?.toDataURL()
                                                ? `<div class="grid-item">
                                                           <span style="font-size: 16px;">BMI For Age</span>
                                                           <img width="100%" height="80%" src="${bmiToAgeCanvas?.toDataURL()}" />
                                                    </div>`
                                                : ''
                                        } 
                                         ${
                                             ofcToAgeCanvas?.toDataURL()
                                                 ? `<div class="grid-item">
                                                           <span style="font-size: 16px;">OFC For Age</span>
                                                           <img width="100%" height="80%" src="${ofcToAgeCanvas?.toDataURL()}" />
                                                    </div>`
                                                 : ''
                                         }  
                                         ${
                                             weightToHeightCanvas?.toDataURL()
                                                 ? `<div class="grid-item">
                                                          <span style="font-size: 16px;">Weight For Height</span>
                                                          <img width="100%" height="80%" src="${weightToHeightCanvas?.toDataURL()}" />
                                                    </div>`
                                                 : ''
                                         }    
                                            </div>
                                         </div>
                                        ${footerHtml}
                                      </div>
                                     </body>
                                </html>
                            `);

        iframeDocument?.close();

        printWindow.onload = () => {
            track('GROWTH_CHART_PRINT');
            printWindow?.contentWindow?.print();
            const doc = printWindow.contentDocument;
            if (doc) {
                doc.body.innerHTML = '';
            }
        };
    };

    render() {
        const { closeMe, patientPersonalProfile, vitals, visits } = this.props;
        const { selectedTab, isPreTerm, growthChartData, showGestationalError } = this.state;
        const age = patientPersonalProfile?.age;
        const name = patientPersonalProfile?.name;
        const ageString = getAgeString(
            {
                age: patientPersonalProfile?.age?.value,
                month: patientPersonalProfile?.age?.month,
                dob: patientPersonalProfile?.age?.dob,
            },
            undefined,
            '',
        );

        const gender = patientPersonalProfile?.gender as 'male' | 'female';
        const height = vitals.find((v) => v.id === 'lb-7561569471');
        const weight = vitals.find((v) => v.id === 'lb-7991006736');
        const ofc = vitals.find((v) => v.id === 'lb-7205446246');
        const gestationalAge = vitals.find((v) => v.id === 'lb-5257276261');
        const bmi = vitals.find((v) => v.id === 'lb-5305097546');

        const ageInNumber = getAgeInYearsWithoutRoundOff(age);

        if (!growthChartData) {
            return null;
        }

        return (
            <Hotkeys keyName="esc" onKeyDown={closeMe}>
                <SlideFromRightWrapper closeMe={closeMe} cssClass="w-90p">
                    <div
                        className="flex flex-col h-full space-between w-full relative"
                        style={{
                            backgroundColor: '#FFF',
                        }}
                    >
                        <div className="flex items-center space-x-16 p-16 border-b border-neutral-8">
                            <div
                                onClick={closeMe}
                                className="flex items-center justify-center w-16 h-16"
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="w-16 h-16 fill-darwin-neutral-1000"
                                >
                                    <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z"></path>
                                </svg>
                            </div>
                            <p className="text-darwin-neutral-1000 heading-2 bold">Growth Chart</p>

                            <span className="text-12 font-700 text-darwin-neutral_new-800">
                                <span>{name ? `${name} | ` : ''}</span>
                                {gender ? `${ONE_CHAR_GENDER[gender?.toLocaleLowerCase()]} | ` : ''}
                                {ageString ? `${ageString}` : ''}
                            </span>
                        </div>

                        <div
                            id="growth-chats-ids"
                            className="flex items-center justify-center h-full"
                        >
                            {isPreTerm ? (
                                <div className="w-60p border-b border-neutral-8 select-none bg-white h-full">
                                    <div className="flex items-center font-600 text-14 leading-28 cursor-pointer border-b border-neutral-8 pt-10 px-16">
                                        <div
                                            className={
                                                selectedTab === 'weight-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('weight-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'weight-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Weight For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'height-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('height-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'height-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Height For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'ofc-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('ofc-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'ofc-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                OFC For Age
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col h-full overflow-hidden">
                                        <div
                                            onScroll={() => {
                                                this.debouncedHandleScroll();
                                            }}
                                            className="h-full overflow-auto hide-scrollbar"
                                        >
                                            <div
                                                id="weight-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <WeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    weight={weight}
                                                    gestationalAge={gestationalAge}
                                                    isPreTerm={isPreTerm}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="height-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <HeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    height={height}
                                                    gestationalAge={gestationalAge}
                                                    isPreTerm={isPreTerm}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="ofc-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <OFCToAge
                                                    age={age}
                                                    gender={gender}
                                                    ofc={ofc}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    isPreTerm={isPreTerm}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div className="h-140"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : ageInNumber <= 5 ? (
                                <div className="w-60p border-b border-neutral-8 select-none bg-white h-full">
                                    <div className="flex items-center font-600 text-14 leading-28 cursor-pointer border-b border-neutral-8 pt-10 px-16">
                                        <div
                                            className={
                                                selectedTab === 'weight-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('weight-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'weight-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Weight For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'height-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('height-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'height-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Height For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'ofc-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('ofc-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'ofc-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                OFC For Age
                                            </p>
                                        </div>

                                        <div
                                            className={
                                                selectedTab === 'weight-for-height'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('weight-for-height');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'weight-for-height'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Weight For Height
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col h-full overflow-hidden">
                                        <div
                                            onScroll={() => {
                                                this.debouncedHandleScroll();
                                            }}
                                            className="h-full overflow-auto hide-scrollbar"
                                        >
                                            <div
                                                id="weight-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <WeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    weight={weight}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="height-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <HeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    height={height}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="ofc-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <OFCToAge
                                                    age={age}
                                                    gender={gender}
                                                    ofc={ofc}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="weight-for-height"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <WeightToHeight
                                                    age={age}
                                                    gender={gender}
                                                    weight={weight}
                                                    height={height}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div className="h-140"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : 5 < ageInNumber && ageInNumber <= 18 ? (
                                <div className="w-60p border-b border-neutral-8 select-none bg-white h-full">
                                    <div className="flex items-center font-600 text-14 leading-28 cursor-pointer border-b border-neutral-8 pt-10 px-16">
                                        <div
                                            className={
                                                selectedTab === 'weight-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('weight-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'weight-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Weight For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'height-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('height-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'height-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                Height For Age
                                            </p>
                                        </div>
                                        <div
                                            className={
                                                selectedTab === 'bmi-for-age'
                                                    ? 'border-b-2 mx-16 border-actionPrimary  text-14 font-700'
                                                    : 'border-b-2 mx-16 border-transparent text-14 font-700'
                                            }
                                            onClick={() => {
                                                this.selectGraphSection('bmi-for-age');
                                            }}
                                        >
                                            <p
                                                className={
                                                    selectedTab === 'bmi-for-age'
                                                        ? 'pb-8 text-actionPrimary text-14 font-700 whitespace-nowrap'
                                                        : 'pb-8 text-neutral-3 text-14 font-700 whitespace-nowrap'
                                                }
                                            >
                                                BMI For Age
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col h-full overflow-hidden">
                                        <div
                                            onScroll={() => {
                                                this.debouncedHandleScroll();
                                            }}
                                            className="h-full overflow-auto hide-scrollbar"
                                        >
                                            <div
                                                id="weight-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <WeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    weight={weight}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="height-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <HeightToAge
                                                    age={age}
                                                    gender={gender}
                                                    height={height}
                                                    gestationalAge={gestationalAge}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div
                                                id="bmi-for-age"
                                                className="border-b border-darwin-neutral_new-200 py-16 relative"
                                            >
                                                <BMIToAge
                                                    age={age}
                                                    gender={gender}
                                                    bmi={bmi}
                                                    growthChartData={growthChartData}
                                                    visits={visits}
                                                />
                                            </div>
                                            <div className="h-140"></div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="w-60p select-none bg-white h-full space-y-24 flex flex-col items-center justify-center">
                                    <div>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="26"
                                            height="24"
                                            viewBox="0 0 26 24"
                                            fill="none"
                                        >
                                            <path
                                                d="M13 24C8.6875 24 4.75 21.75 2.59375 18C0.4375 14.2969 0.4375 9.75 2.59375 6C4.75 2.29688 8.6875 0 13 0C17.2656 0 21.2031 2.29688 23.3594 6C25.5156 9.75 25.5156 14.2969 23.3594 18C21.2031 21.75 17.2656 24 13 24ZM13 6C12.3438 6 11.875 6.51562 11.875 7.125V12.375C11.875 13.0312 12.3438 13.5 13 13.5C13.6094 13.5 14.125 13.0312 14.125 12.375V7.125C14.125 6.51562 13.6094 6 13 6ZM11.5 16.5C11.5 17.3438 12.1562 18 13 18C13.7969 18 14.5 17.3438 14.5 16.5C14.5 15.7031 13.7969 15 13 15C12.1562 15 11.5 15.7031 11.5 16.5Z"
                                                fill="#767676"
                                            />
                                        </svg>
                                    </div>
                                    <span>
                                        Growth Chart can only be generate for age range between 0 to
                                        18 or pre term
                                    </span>
                                </div>
                            )}

                            <div className="w-40p bg-darwin-neutral_new-50 h-full p-16 border-l border-neutral-8">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-8">
                                        <span className="text-14">Pre-Term child?</span>
                                        <Switch
                                            className="link-color"
                                            checked={isPreTerm}
                                            onChange={this.onPretermChange}
                                            style={{
                                                backgroundColor: isPreTerm
                                                    ? '#215FFF'
                                                    : 'rgba(0,0,0,.25)',
                                            }}
                                        />
                                    </div>
                                    <div className="p-7 text-12 font-700 bg-white rounded-4 border border-darwin-neutral_new-200">
                                        {isPreTerm ? 'Fenton Chart' : 'WHO/IAP Chart'}
                                    </div>
                                </div>

                                <div>
                                    <GrowthVitals
                                        onElementDelete={this.onElementDelete}
                                        onElementUpdate={this.onElementUpdate}
                                        elements={vitals}
                                        vitalConfigData={getWhiteListedGrowthChartVitalsByAge(
                                            ageInNumber,
                                            isPreTerm,
                                        )}
                                        isPreTerm={isPreTerm}
                                        visits={visits}
                                        showGestationalError={showGestationalError}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="absolute bottom-0 flex items-center justify-between w-full bg-white px-16 py-8 border-t border-neutral-8">
                            <button
                                onClick={closeMe}
                                className="secondary-btn px-16 py-8 body-2 space-x-8 font-400"
                            >
                                Close
                            </button>
                            <div className="space-x-8">
                                <button
                                    onClick={this.printGraph}
                                    className="primary-btn px-16 py-8 body-2 space-x-8 font-400"
                                >
                                    Print
                                </button>
                                <button
                                    onClick={closeMe}
                                    className="primary-btn px-16 py-8 body-2 space-x-8 font-400"
                                >
                                    Done
                                </button>
                            </div>
                        </div>
                    </div>
                    <iframe id={GROWTH_CHART_PRINT_TYPE} className="h-0 overflow-auto"></iframe>
                </SlideFromRightWrapper>
            </Hotkeys>
        );
    }
}

export default connect((state: GlobalState) => ({
    patientPersonalProfile: getPatientPersonalProfile(state),
    prescription: getPrescription(state),
    vitals: getVitals(state),
    visits: [...(getPatientDetailsVisits(state)?.data || [])]?.sort((a, b) => {
        const dateA = moment(a.date);
        const dateB = moment(b.date);
        if (dateA.isBefore(dateB)) return -1;
        if (dateA.isAfter(dateB)) return 1;
        return 0;
    }),
    doctor: getRxDoctor(state),
}))(GrowthChartRightPopUp);
